<template>
  <div class="summary-list" v-loading="loading">
    <WelcomeBanner title="工作总结" :subtitle="`总汇报：${total} 条`">
      <template #right>
        <el-tag type="primary" effect="plain">日报：{{ typeStats.daily }} 条</el-tag>
        <el-tag type="success" effect="plain">周报：{{ typeStats.weekly }} 条</el-tag>
        <el-tag type="warning" effect="plain">月报：{{ typeStats.monthly }} 条</el-tag>
      </template>
    </WelcomeBanner>

    <!-- 筛选区 -->
    <BaseCard class="filter-section">
      <div class="filter-row">
        <!-- 机器人筛选 -->
        <div class="filter-item">
          <span class="filter-label">机器人：</span>
          <el-select
            v-model="filters.robotId"
            placeholder="请选择机器人"
            clearable
            @change="handleFilterChange"
            filterable
          >
            <el-option
              v-for="robot in robotOptions"
              :key="robot.id"
              :label="robot.name"
              :value="robot.id"
            />
          </el-select>
        </div>

        <!-- 类型筛选 -->
        <div class="filter-item">
          <span class="filter-label">汇报类型：</span>
          <el-select
            v-model="filters.type"
            placeholder="全部类型"
            clearable
            @change="handleFilterChange"
          >
            <el-option label="全部类型" value="" />
            <el-option
              v-for="item in typeOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </div>

        <!-- 时间范围筛选 -->
        <div class="filter-item">
          <span class="filter-label">时间范围：</span>
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            @change="handleDateChange"
            align="right"
          />
        </div>

        <!-- 重置按钮 -->
        <div class="filter-actions">
          <el-button @click="handleReset">重置</el-button>
          <el-button type="primary" @click="loadSummaryList">查询</el-button>
        </div>
      </div>
    </BaseCard>

    <!-- 加载状态 -->
    <div v-if="!list.length && loading" class="loading-wrapper">
      <span class="loading-text">加载中...</span>
    </div>

    <!-- 空状态 -->
    <div v-else-if="list.length === 0" class="empty-wrapper">
      <el-empty description="暂无工作总结数据" />
    </div>

    <!-- 汇报卡片列表 -->
    <BaseCard class="summary-card-container" v-else>
      <div
        v-for="summary in filteredList"
        :key="summary.id"
        class="summary-card"
        @click="goToDetail(summary.id)"
      >
        <div class="summary-card-header">
          <div class="summary-info">
            <h4 class="summary-title">{{ summary.title }}</h4>
            <div class="summary-meta">
              <span class="meta-item">
                <i class="el-icon-user-solid"></i>
                {{ summary.robot_name || summary.robotName || `机器人 ${summary.robot_id}` }}
              </span>
              <span class="meta-item">
                <i class="el-icon-document"></i>
                {{ getTypeName(summary.type) }}
              </span>
              <span class="meta-item">
                <i class="el-icon-time"></i>
                {{ formatDate(summary.createdAt) }}
              </span>
            </div>
          </div>
          <div class="summary-type">
            <el-tag :type="getTypeTagType(summary.type)">
              {{ getTypeName(summary.type) }}
            </el-tag>
          </div>
        </div>

        <div class="summary-card-body">
          <p class="summary-preview">{{ summary.preview }}</p>
          <div v-if="summary.projectName" class="summary-project">
            <i class="el-icon-folder-opened"></i> 项目：{{ summary.projectName }}
          </div>
        </div>

        <div class="summary-card-footer">
          <div class="summary-stats">
            <span v-if="summary.wordCount > 0" class="stat-item">
              <i class="el-icon-document-copy"></i>
              {{ summary.wordCount }} 字
            </span>
            <span v-if="summary.completionRate >= 0" class="stat-item">
              <i class="el-icon-data-analysis"></i>
              完成率：{{ summary.completionRate }}%
            </span>
          </div>
          <div class="summary-action">
            <el-button type="text" icon="el-icon-view">查看详情</el-button>
          </div>
        </div>
      </div>
    </BaseCard>

    <!-- 分页 -->
    <div v-if="total > 0" class="pagination-wrapper">
      <el-pagination
        background
        layout="total, prev, pager, next, jumper"
        :total="total"
        :page-size="pageSize"
        :current-page="currentPage"
        @current-change="handlePageChange"
      />
    </div>
  </div>
</template>

<script>
import { getMySummaryList } from '../api/summary'
import { getRobotList } from '../api/robot'
import BaseCard from '@/components/BaseCard.vue'
import WelcomeBanner from '@/components/WelcomeBanner.vue'

export default {
  name: 'SummaryList',
  components: { BaseCard, WelcomeBanner },
  data() {
    return {
      loading: false,
      list: [], // 当前页列表
      robotOptions: [], // 机器人选项
      filters: {
        robotId: '',
        type: ''
      },
      dateRange: [],
      currentPage: 1,
      pageSize: 10,
      total: 0, // 总记录数
      // 类型统计
      typeStats: {
        daily: 0,
        weekly: 0,
        monthly: 0
      },

      // 类型选项
      typeOptions: [
        { label: '日报', value: 'daily', apiValue: 'D' },
        { label: '周报', value: 'weekly', apiValue: 'W' },
        { label: '月报', value: 'monthly', apiValue: 'M' }
      ],

      // 类型配置
      typeConfig: {
        daily: { text: '日报', tagType: 'primary', apiValue: 'D' },
        weekly: { text: '周报', tagType: 'success', apiValue: 'W' },
        monthly: { text: '月报', tagType: 'warning', apiValue: 'M' }
      }
    }
  },
  computed: {
    // 过滤后的列表（前端再按机器人筛选）
    filteredList() {
      let result = this.list
      // 机器人筛选（前端过滤）
      if (this.filters.robotId) {
        result = result.filter(item => item.robot_id === this.filters.robotId)
      }
      return result
    }
  },
  mounted() {
    this.loadRobotOptions()
    this.loadSummaryList()
  },
  methods: {
    // 加载机器人选项
    async loadRobotOptions() {
      try {
        const data = await getRobotList()
        if (data && data.list) {
          this.robotOptions = data.list.map(robot => ({
            id: robot.robot_id,
            name: robot.robot_name
          }))
        } else if (Array.isArray(data)) {
          this.robotOptions = data.map(robot => ({
            id: robot.robot_id,
            name: robot.robot_name
          }))
        } else {
          this.generateMockRobotOptions()
        }
      } catch (error) {
        console.error('加载机器人选项失败', error)
        this.generateMockRobotOptions()
      }
    },

    // 生成模拟机器人选项
    generateMockRobotOptions() {
      const roles = [
        '产品经理', '开发工程师', '测试工程师', 
        '设计师', '运营分析师', '文案策划'
      ]
      this.robotOptions = roles.map((role, index) => ({
        id: index + 1,
        name: `${role}机器人${index + 1}`
      }))
    },

    // 加载工作总结列表
    async loadSummaryList() {
      this.loading = true
      try {
        // 构建查询参数
        const params = {
          page: this.currentPage,
          page_size: this.pageSize,
          summary_type: this.filters.type ? this.typeConfig[this.filters.type]?.apiValue : null
        }

        // 添加日期范围参数
        if (this.dateRange && this.dateRange.length === 2) {
          const formatDate = (date) => {
            const d = new Date(date)
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
          }
          params.start_date = formatDate(this.dateRange[0])
          params.end_date = formatDate(this.dateRange[1])
        }

        // 调用新的统一接口
        const result = await getMySummaryList(params)

        // 处理返回数据
        const list = result?.list || []
        this.total = result?.total || 0
        this.typeStats = {
          daily: result?.daily_count || 0,
          weekly: result?.weekly_count || 0,
          monthly: result?.monthly_count || 0
        }

        this.list = list.map(item => ({
          id: item.summary_id,
          title: item.summary_type_name || this.getTypeName(item.summary_type?.toLowerCase()) + ' - ' + item.summary_date,
          preview: item.content ? item.content.substring(0, 100) + (item.content.length > 100 ? '...' : '') : '',
          robot_id: item.robot_id,
          robot_name: item.robot_name || '',
          projectName: '',
          type: item.summary_type?.toLowerCase() || 'daily',
          createdAt: item.create_time ? new Date(item.create_time).getTime() : Date.now(),
          summaryDate: item.summary_date,
          wordCount: item.content ? item.content.length : 0,
          completionRate: -1
        }))
      } catch (error) {
        this.$message.error('加载工作总结列表失败')
        console.error(error)
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },

    // 筛选条件变化
    handleFilterChange() {
      this.currentPage = 1
      this.loadSummaryList()
    },

    // 日期变化
    handleDateChange() {
      this.handleFilterChange()
    },

    // 重置筛选
    handleReset() {
      this.filters = {
        robotId: '',
        type: ''
      }
      this.dateRange = []
      this.currentPage = 1
      this.loadSummaryList()
    },

    // 分页变化
    handlePageChange(page) {
      this.currentPage = page
      this.loadSummaryList()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    // 获取类型标签类型
    getTypeTagType(type) {
      return this.typeConfig[type]?.tagType || 'info'
    },

    // 获取类型名称
    getTypeName(type) {
      return this.typeConfig[type]?.text || '未知'
    },

    // 格式化日期
    formatDate(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    // 跳转详情页
    goToDetail(id) {
      this.$router.push({ name: 'SummaryDetail', params: { id } })
    }
  }
}
</script>

<style lang="scss" scoped>
.summary-list {
  padding: 16px;
  min-height: calc(100vh - 100px);
  background: linear-gradient(180deg, #f5f7fa 0%, #fff 100%);
}

/* 页面标题 */
.welcome-banner { margin-bottom: 16px }

/* 筛选区 */
.filter-section {
  padding: 12px 16px;
  margin-bottom: 16px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}

.filter-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-md);
}

.filter-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.filter-label {
  font-size: var(--font-size-base);
  color: var(--text-regular);
  white-space: nowrap;
}

.filter-item :deep(.el-select) {
  width: 180px;
}

.filter-item :deep(.el-date-picker) {
  width: 300px;
}

.filter-actions {
  margin-left: auto;
}

/* 卡片容器 */
.summary-card-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
}

/* 汇报卡片 */
.summary-card {
  position: relative;
  padding: 16px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  cursor: pointer;
  transition: all 0.28s ease;
  border: 1px solid rgba(0,0,0,0.04);
  overflow: hidden;

  &:hover {
    box-shadow: 0 8px 28px rgba(0,0,0,0.10);
    transform: translateY(-4px) scale(1.01);
    border-color: rgba(99,102,241,0.2);
  }

  &::before {
    content: '';
    position: absolute;
    top: -12px;
    right: -28px;
    width: 110px;
    height: 110px;
    background: linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(139,92,246,0.08) 100%);
    border-radius: 0 50% 0 50%;
    transform: rotate(12deg);
    pointer-events: none;
  }
}

.summary-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-sm);
}

.summary-info {
  flex: 1;
}

.summary-title {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 700;
  color: #333;
}

.summary-meta {
  display: flex;
  gap: var(--spacing-lg);
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.summary-type {
  flex-shrink: 0;
  margin-left: var(--spacing-md);
}

.summary-card-body {
  margin-bottom: var(--spacing-sm);
}

.summary-preview {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #51585f;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.summary-project {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 4px;
}

.summary-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: var(--spacing-sm);
  border-top: 1px solid var(--border-color-lighter);
}

.summary-stats {
  display: flex;
  gap: var(--spacing-lg);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-sm);
  color: var(--text-regular);
}

.summary-action {
  color: var(--primary-color);
}

/* 分页 */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  padding: 12px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}

/* 加载和空状态 */
.loading-wrapper,
.empty-wrapper {
  padding: 60px var(--spacing-lg);
  background-color: var(--bg-card);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
}

/* 响应式适配 */
@media screen and (max-width: 768px) {
  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-item {
    width: 100%;

    :deep(.el-select),
    :deep(.el-date-picker) {
      flex: 1;
      width: auto;
    }
  }

  .filter-actions {
    margin-left: 0;
    display: flex;
    gap: var(--spacing-xs);

    :deep(.el-button) {
      flex: 1;
    }
  }

  .summary-card-header {
    flex-direction: column;
  }

  .summary-type {
    margin-left: 0;
    margin-top: var(--spacing-xs);
    align-self: flex-start;
  }

  .summary-meta {
    flex-direction: column;
    gap: 4px;
  }

  .summary-card-footer {
    flex-direction: column;
    gap: var(--spacing-sm);
    align-items: flex-start;
  }

  .summary-stats {
    flex-direction: column;
    gap: 4px;
  }
}
</style>